// Slug helpers for the /event/[slug] page. The two functions cover the
// niche where Polymarket exposes a question string but not the canonical
// market slug — for crypto-target markets we reconstruct it from the
// question pattern so we can deep-link / route correctly. Pure helpers,
// no React, no DOM.

export function slugifyPolymarketSegment(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

/**
 * Reconstruct the canonical Polymarket crypto market slug from a
 * question string. PM emits questions of the form
 *   "Will Bitcoin hit $X - January 5, 12:00 PM ET"
 * but the API doesn't always carry the slug; this rebuilds the
 * `<topic>-jan-5-2026-12pm-et` form Polymarket uses internally.
 * Returns null if the question doesn't match the expected shape.
 */
export function buildCanonicalPolymarketCryptoMarketSlug(
  question: string | null | undefined,
  endDate: string | null | undefined,
): string | null {
  if (!question) return null;

  const normalizedQuestion = question
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

  const match = normalizedQuestion.match(
    /^(.*?)\s*-\s*([A-Za-z]+)\s+(\d{1,2}),\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)(?:\s*-\s*\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)?\s*ET$/i
  );

  if (!match) return null;

  const [, baseTitle, month, day, rawHour, rawMinute = "00", rawMeridiem] = match;
  const year = endDate ? new Date(endDate).getUTCFullYear() : new Date().getUTCFullYear();
  const hour = String(parseInt(rawHour, 10));
  const meridiem = rawMeridiem.toLowerCase();
  const minute = rawMinute.padStart(2, "0");
  const timeSlug = minute === "00" ? `${hour}${meridiem}` : `${hour}-${minute}${meridiem}`;

  return `${slugifyPolymarketSegment(baseTitle)}-${month.toLowerCase()}-${day}-${year}-${timeSlug}-et`;
}
